import { ComponentProps, useState } from 'react';
import { LoadingButton } from '@/components/LoadingButton';
import { CreditCard } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOrgs } from '@/contexts/OrgsContext';
import { useRoles } from '@/contexts/RolesContext';
import { useGetOrgProductPlans } from '@/api/auth/billing/billing';
import { PlanPickerDialog } from '@/components/billing/PlanPickerDialog';
import { DATA_MISSION_PRODUCT_ID } from '@/constants/products';

interface SubscribeButtonProps {
  variant?: ComponentProps<typeof LoadingButton>['variant'];
  size?: ComponentProps<typeof LoadingButton>['size'];
  className?: string;
}

/**
 * Button that opens the plan picker for subscribing to the product.
 * The eligible plans are resolved server-side by the Auth Service; when the
 * organization has no purchasable plans the button is hidden entirely.
 */
export function SubscribeButton({ variant = 'secondary', size, className }: SubscribeButtonProps) {
  const { t, language } = useLanguage();
  const { selectedOrganizationId } = useOrgs();
  const { isAdmin, isBillingManager } = useRoles();
  const [pickerOpen, setPickerOpen] = useState(false);

  const canSubscribe = (isAdmin || isBillingManager) && !!selectedOrganizationId;

  const { data: plansResponse, isLoading } = useGetOrgProductPlans(
    selectedOrganizationId ?? '',
    DATA_MISSION_PRODUCT_ID,
    {
      query: {
        enabled: canSubscribe,
      },
      request: { headers: { 'Accept-Language': language } },
    }
  );

  const plans = plansResponse?.data.plans;

  // The plans/checkout endpoints require the Administrator or Billing org role.
  if (!canSubscribe) {
    return null;
  }

  // The server resolved the org to no purchasable plans — hide Subscribe (no reason is leaked).
  if (plans?.length === 0) {
    return null;
  }

  return (
    <>
      <LoadingButton
        variant={variant}
        size={size}
        className={className}
        onClick={() => {
          setPickerOpen(true);
        }}
        disabled={isLoading}
        isLoading={isLoading}
      >
        <CreditCard className="h-4 w-4" />
        {t.billing.subscribe}
      </LoadingButton>
      <PlanPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} productId={DATA_MISSION_PRODUCT_ID} />
    </>
  );
}
